import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Loader2, Search, ExternalLink, UtensilsCrossed, Bus, Heart, Shield, Home, BookOpen, Laptop, PartyPopper, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORY_META = {
  food: { icon: UtensilsCrossed, color: '#f59e0b', label: 'Food & Meals' },
  transport: { icon: Bus, color: '#ec4899', label: 'Transport' },
  health: { icon: Heart, color: '#00B894', label: 'Health & Wellness' },
  insurance: { icon: Shield, color: '#8b5cf6', label: 'Insurance' },
  housing: { icon: Home, color: '#3b82f6', label: 'Real Estate & Housing' },
  education: { icon: BookOpen, color: '#06b6d4', label: 'Education & Training' },
  devices: { icon: Laptop, color: '#f97316', label: 'Devices & Work Tools' },
  lifestyle: { icon: PartyPopper, color: '#d946ef', label: 'Lifestyle & Entertainment' },
};

export default function EmployeeVendors() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('wallet_token');
        // Use employee benefits/vendors endpoint
        const res = await fetch(`${API_URL}/api/employee/benefits/vendors`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          setVendors(await res.json());
        } else {
          // Fallback: try company-admin vendors endpoint
          const res2 = await fetch(`${API_URL}/api/company-admin/vendors`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (res2.ok) setVendors(await res2.json());
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    // Check URL params for category filter
    const params = new URLSearchParams(window.location.search);
    if (params.get('category')) setActiveCategory(params.get('category'));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const categories = ['all', ...Object.keys(CATEGORY_META)];
  const filtered = vendors.filter(v => {
    const matchSearch = !search || v.name?.toLowerCase().includes(search.toLowerCase()) || v.description?.toLowerCase().includes(search.toLowerCase());
    const matchCategory = activeCategory === 'all' || v.category === activeCategory;
    return matchSearch && matchCategory;
  });

  const grouped = {};
  filtered.forEach(v => {
    const cat = v.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(v);
  });

  return (
    <div className="space-y-5 pb-6" data-testid="employee-vendors">
      <h1 className="text-xl font-bold text-gray-900">Benefit Vendors</h1>
      <p className="text-sm text-gray-500">Browse approved vendors where you can use your benefits.</p>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} className="h-9 pl-9" />
      </div>

      {/* Category Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {categories.map(cat => {
          const meta = CATEGORY_META[cat];
          const Icon = meta?.icon;
          return (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}>
              {Icon && <Icon className="w-3 h-3" />}
              {cat === 'all' ? 'All' : meta?.label || cat}
            </button>
          );
        })}
      </div>

      {/* Vendor Grid by Category */}
      {Object.entries(grouped).map(([cat, catVendors]) => {
        const meta = CATEGORY_META[cat] || { label: cat, color: '#9ca3af' };
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-3">
              {meta.icon && <meta.icon className="w-4 h-4" style={{ color: meta.color }} />}
              <h2 className="text-sm font-bold text-gray-900">{meta.label || cat}</h2>
              <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{catVendors.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {catVendors.map(v => (
                <Card key={v.id || v.slug} className="border-gray-200 hover:border-pink-300 hover:shadow-sm transition-all cursor-pointer" onClick={() => navigate(`/app/employee/benefits/vendors/${v.id}`)} data-testid={`vendor-card-${v.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-gray-50 border border-gray-100">
                        {v.logo_url ? (
                          <img src={v.logo_url} alt={v.name} className="w-6 h-6 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <span className="text-[10px] font-bold text-gray-400">
                            {v.name?.slice(0, 2).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-gray-900 truncate">{v.name}</p>
                          <ChevronRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                        </div>
                        <p className="text-[10px] text-gray-400 capitalize">{v.sub_category?.replace(/_/g, ' ') || v.category}</p>
                        <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">{v.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {filtered.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No vendors found</p>}
    </div>
  );
}
